import { useEffect, useMemo } from 'react';
import { FolderStructureType, useFolderStructureVisibility } from '@/contexts/FolderStructureVisibilityContext';
import { sidebarGroups } from '@/lib/pages';
import { hasLicenseAccess } from '@/lib/utils/licenseConfig';

interface UseFolderTypeManagementProps {
  selectedGame: any;
  selectedCompany: any;
}

// Map page ID to folder structure type
const PAGE_ID_TO_FOLDER_TYPE: Record<string, FolderStructureType> = {
  'blueprints': 'blueprints',
  'glossary': 'library',
  'dialogs': 'dialogs',
  'variables': 'variables',
  'documents': 'documents',
  'logic': 'flows',
};

export function useFolderTypeManagement({ selectedGame, selectedCompany }: UseFolderTypeManagementProps) {
  const {
    setAvailableFolderTypes,
  } = useFolderStructureVisibility();

  // Determine which folder structures should be shown based on license and sidebar pages
  const availableFolderTypes = useMemo((): FolderStructureType[] => {
    if (!selectedCompany?.license || !selectedGame?.id) return [];
    
    const folderTypes: FolderStructureType[] = [];

    
    // Check each sidebar item
    sidebarGroups.forEach(group => {
      group.items.forEach(item => {
        // Only include items that require a game (content pages)
        if (!item.requiresGame) return;
        
        // Check if this page has a folder structure type
        const folderType = PAGE_ID_TO_FOLDER_TYPE[item.id];
        if (!folderType) return;
        
        // Check if user has license for this feature
        const hasAccess = hasLicenseAccess(selectedCompany.license, item.requiredLicense || null);
        
        if (hasAccess && !folderTypes.includes(folderType)) {
          folderTypes.push(folderType);
        }
      });
    });
    
    return folderTypes;
  }, [selectedCompany?.license, selectedGame?.id]);

  // Update available folder types in context whenever they change
  useEffect(() => {
    if (!selectedGame?.id) {
      return;
    }
    
    setAvailableFolderTypes(availableFolderTypes);
  }, [availableFolderTypes, selectedGame?.id, setAvailableFolderTypes]);

  const showFolderStructure = selectedGame?.id && availableFolderTypes.length > 0;


  return {
    availableFolderTypes,
    showFolderStructure,
  };
}
